// src/components/social_login/MicrosoftRedirectHandler.tsx
import { createData } from "api/apiHelpers";
import { useSocialAuth } from "hooks/useAuth";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { loginRequest, msalInstance } from "../../msalConfig";
import { useTranslation } from "../../i18n";
import logger from "utils/logger";

export default function MicrosoftRedirectHandler() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { handleSocialAuthSuccess } = useSocialAuth();

  useEffect(() => {
    (async () => {
      try {
        logger.debug("🔹 MicrosoftRedirectHandler started");

        await msalInstance.handleRedirectPromise().then((response) => {
          if (response) {
            logger.debug("MSAL redirect response:", response);
          }
        });
        const accounts = msalInstance.getAllAccounts();
        logger.debug("MSAL accounts after redirect:", accounts);

        if (accounts.length === 0) {
          logger.error("❌ No MSAL accounts found");
          // navigate("/login");
          return;
        }

        let result;
        try {
          result = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
        } catch (e) {
          logger.warn("⚠️ Silent token acquisition failed:", e);
          try {
            result = await msalInstance.acquireTokenPopup(loginRequest);
          } catch (popupError) {
            logger.error(
              "❌ Popup token acquisition also failed:",
              popupError
            );
            // navigate("/login");
            return;
          }
        }

        if (!result || !result.accessToken) {
          logger.error("❌ No accessToken received from MSAL result:", result);
          // navigate("/login");
          return;
        }

        logger.debug(
          "✅ Got Microsoft Graph accessToken:",
          result.accessToken.substring(0, 20),
          "..."
        );

        logger.debug("🔹 Posting token to backend /auth/microsoft/login/");
        const backendResponse = await createData<{ access_token: string }, any>(
          "/auth/microsoft/login/",
          { access_token: result.accessToken }
        );

        logger.debug("✅ Backend responded with JWT:", backendResponse);

        await handleSocialAuthSuccess(backendResponse);
      } catch (err) {
        logger.error("❌ Microsoft redirect handler error:", err);
        // navigate("/login");
      }
    })();
  }, [navigate, handleSocialAuthSuccess]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: "60vh",
        gap: 2,
      }}
    >
      <CircularProgress
        size={40}
        sx={{ color: theme.palette.primary.main }}
      />
      <Typography
        variant="body1"
        sx={{
          color: theme.custom?.contentText?.secondary || theme.palette.text.secondary,
          fontWeight: 500,
        }}
      >
        {t("auth.completing_microsoft", "Completing Microsoft login…")}
      </Typography>
    </Box>
  );
}
